import type { UseQueryOptions } from '@tanstack/react-query';
import type { Portfolio } from './usePortfolios';
import { usePortfolios } from './usePortfolios';

interface ActivePortfolio {
  activePortfolio: Portfolio | undefined;
  portfolioId: string;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Single source of truth for "which portfolio is active": today that's
 * `portfolios[0]`, since the app doesn't support switching between multiple
 * portfolios yet. Centralizing the derivation here (rather than each caller
 * re-deriving `portfolios?.[0]`) means multi-portfolio support only needs to
 * change this one hook.
 *
 * Prefer this over `useActivePortfolioData` when a caller doesn't also need
 * the active portfolio's accounts — the latter always fetches them.
 */
export function useActivePortfolio(
  options?: Omit<UseQueryOptions<Portfolio[]>, 'queryKey' | 'queryFn'>
): ActivePortfolio {
  const { data: portfolios, isLoading, isError } = usePortfolios(options);
  const activePortfolio = portfolios?.[0];
  const portfolioId = activePortfolio?.id ?? '';

  return { activePortfolio, portfolioId, isLoading, isError };
}
